import { Dialog, Stack } from '@optimus-web/core';
import { AvailabilityBlock } from '@mews-commander/data-access';
import { DataTestId, TextKey } from '@mews-commander/enums';
import {
    LocalizedText,
    createZodFormResolver,
    lt,
    showSuccessAlert,
    SubmitHandler,
    useFormControls,
} from '@mews/framework';
import { getResourceCategoryName } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { useCommanderSelector } from '@mews-commander/utils';
import { FormValues, OverrideAvailabilityAdjustmentFields, formSchema } from './OverrideAvailabilityAdjustmentFields';
import { OverrideReleaseDialogData } from './types';
import { useBlockReleaseOverrideMutation } from './useBlockReleaseOverrideMutation';

interface OverrideReleaseAdjustmentDialogProps {
    closeDialog: () => void;
    availabilityBlock: AvailabilityBlock;
    data: OverrideReleaseDialogData | null;
}

export const OverrideAvailabilityReleaseDialog = ({
    availabilityBlock,
    closeDialog,
    data,
}: OverrideReleaseAdjustmentDialogProps) => {
    const { serviceId, startUtc, endUtc, releaseStrategy } = availabilityBlock;

    const resourceCategory = useCommanderSelector(
        (s) => (data && getResourceCategoryName(s, { serviceId, id: data.resourceCategoryId })) ?? ''
    );
    const overrideUpdateMutation = useBlockReleaseOverrideMutation();

    const onSubmit: SubmitHandler<FormValues> = (values, { reset }) => {
        if (!data) return;

        overrideUpdateMutation.mutate(
            {
                values,
                options: data,
                availabilityBlock,
            },
            {
                onSuccess: () => {
                    closeDialog();
                    reset();
                    showSuccessAlert({ description: lt('SaveSuccessMessage') });
                },
            }
        );
    };

    const { renderFormContent, submit } = useFormControls({
        onSubmit,
        values: data ? { firstTimeUnitStartUtc: data.dateTimeUtc, lastTimeUnitStartUtc: data.dateTimeUtc } : undefined,
        resolver: createZodFormResolver(formSchema),
    });

    return (
        <Dialog
            title={
                data?.isReleaseOverride ? (
                    <LocalizedText
                        textKey={TextKey.AvailabilityBlockRestoreAvailability}
                        substitutes={{ resourceCategory }}
                    />
                ) : (
                    lt('AvailabilityBlockReleaseAdjustments')
                )
            }
            active={data !== null}
            loading={overrideUpdateMutation.isPending}
            onClose={closeDialog}
            actions={[
                {
                    children: lt('GoBack'),
                    onClick: closeDialog,
                    variant: 'ghost',
                    type: 'button',
                },
                {
                    children: data?.isReleaseOverride ? lt('Save') : lt('Release'),
                    onClick: submit,
                    variant: 'primary',
                    type: 'submit',
                    key: data?.isReleaseOverride
                        ? DataTestId.AvailabilityAdjustmentSave
                        : DataTestId.AvailabilityAdjustmentRelease,
                },
            ]}
        >
            {renderFormContent(
                <Stack spacing="200">
                    <OverrideAvailabilityAdjustmentFields
                        startUtc={startUtc}
                        endUtc={endUtc}
                        releaseStrategy={releaseStrategy}
                        serviceId={serviceId}
                    />
                </Stack>
            )}
        </Dialog>
    );
};
